import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
  Heart, MessageCircle, Share2, Image as ImageIcon, Video, 
  Send, MoreHorizontal, Facebook, Twitter, Linkedin, Link2,
  Plus
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

const LOGO_URL = "https://game-invoice.preview.emergentagent.com/api/files/official-whistle/branding/67ca9f95-5248-441e-9eb3-5fc8cf5c72fb/fb28c3a1-55d7-41f0-be32-2ea609158b1e.png";

export const CommunityPage = () => {
  const { api, user } = useAuth();
  const [blogs, setBlogs] = useState([]);
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newPostOpen, setNewPostOpen] = useState(false);
  const [newStoryOpen, setNewStoryOpen] = useState(false);
  const [postForm, setPostForm] = useState({ title: '', content: '', media_urls: [], media_types: [] });
  const [storyForm, setStoryForm] = useState({ media_url: '', media_type: 'image', caption: '' });
  const [uploading, setUploading] = useState(false);

  const fetchData = async () => {
    try {
      const [blogsRes, storiesRes] = await Promise.all([
        api().get('/blogs'),
        api().get('/stories')
      ]);
      setBlogs(blogsRes.data);
      setStories(storiesRes.data);
    } catch (error) {
      console.error('Failed to fetch community data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [api]);

  const handleFileUpload = async (file, type = 'image') => {
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('folder', type === 'video' ? 'videos' : 'images');
      
      const response = await api().post('/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      return response.data.url;
    } catch (error) {
      toast.error('Upload failed');
      return null;
    } finally {
      setUploading(false);
    }
  };

  const handleCreatePost = async () => {
    if (!postForm.title || !postForm.content) {
      toast.error('Title and content are required');
      return;
    }
    
    try {
      await api().post('/blogs', postForm);
      toast.success('Post created!');
      setNewPostOpen(false);
      setPostForm({ title: '', content: '', media_urls: [], media_types: [] });
      fetchData();
    } catch (error) {
      toast.error('Failed to create post');
    }
  };

  const handleCreateStory = async () => {
    if (!storyForm.media_url) {
      toast.error('Please upload media');
      return;
    }
    
    try {
      await api().post('/stories', storyForm);
      toast.success('Story posted!');
      setNewStoryOpen(false);
      setStoryForm({ media_url: '', media_type: 'image', caption: '' });
      fetchData();
    } catch (error) {
      toast.error('Failed to create story');
    }
  };

  const handleLike = async (blogId) => {
    try {
      const response = await api().post(`/blogs/${blogId}/like`);
      fetchData();
    } catch (error) {
      toast.error('Failed to like');
    }
  };

  const handleShare = async (blogId, platform) => {
    const shareUrl = `${window.location.origin}/community/blog/${blogId}`;
    const blog = blogs.find(b => b.id === blogId);
    const text = blog?.title || 'Check this out!';
    
    const urls = {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`,
      twitter: `https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(text)}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`,
      copy: shareUrl
    };
    
    if (platform === 'copy') {
      navigator.clipboard.writeText(shareUrl);
      toast.success('Link copied!');
    } else {
      window.open(urls[platform], '_blank', 'width=600,height=400');
    }
    
    await api().post(`/blogs/${blogId}/share`);
    fetchData();
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = (now - date) / 1000;
    
    if (diff < 60) return 'Just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return date.toLocaleDateString();
  };

  const canPost = user?.role === 'admin' || user?.role === 'referee';
  const canPostStory = user?.role === 'admin';

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-2 border-white border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div data-testid="community-page" className="max-w-2xl mx-auto space-y-6 animate-fade-in">
      {/* Stories */}
      <div className="bg-zinc-900/50 border border-zinc-800 p-4">
        <div className="flex gap-4 overflow-x-auto pb-2">
          {canPostStory && (
            <Dialog open={newStoryOpen} onOpenChange={setNewStoryOpen}>
              <DialogTrigger asChild>
                <button 
                  data-testid="add-story-btn"
                  className="flex-shrink-0 w-20 h-28 bg-zinc-800 border-2 border-dashed border-zinc-600 rounded-lg flex flex-col items-center justify-center hover:border-white transition-colors"
                >
                  <Plus className="w-6 h-6 text-zinc-400" />
                  <span className="text-xs text-zinc-400 mt-1">Add Story</span>
                </button>
              </DialogTrigger>
              <DialogContent className="bg-zinc-900 border-zinc-800">
                <DialogHeader>
                  <DialogTitle className="font-heading uppercase">Create Story</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <div>
                    <input
                      type="file"
                      accept="image/*,video/*"
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          const isVideo = file.type.startsWith('video');
                          const url = await handleFileUpload(file, isVideo ? 'video' : 'image');
                          if (url) {
                            setStoryForm({ ...storyForm, media_url: url, media_type: isVideo ? 'video' : 'image' });
                          }
                        }
                      }}
                      className="w-full text-sm text-zinc-400 file:mr-4 file:py-2 file:px-4 file:rounded-sm file:border-0 file:bg-white file:text-black file:font-bold"
                    />
                  </div>
                  {storyForm.media_url && (
                    <div className="aspect-[9/16] max-h-64 bg-zinc-800 rounded-lg overflow-hidden">
                      {storyForm.media_type === 'video' ? (
                        <video src={storyForm.media_url} className="w-full h-full object-cover" />
                      ) : (
                        <img src={storyForm.media_url} alt="Story preview" className="w-full h-full object-cover" />
                      )}
                    </div>
                  )}
                  <Input
                    placeholder="Add a caption..."
                    value={storyForm.caption}
                    onChange={(e) => setStoryForm({ ...storyForm, caption: e.target.value })}
                    className="bg-zinc-800 border-zinc-700"
                  />
                  <Button
                    onClick={handleCreateStory}
                    disabled={!storyForm.media_url || uploading}
                    className="w-full bg-white text-black hover:bg-zinc-200 font-bold uppercase"
                  >
                    {uploading ? 'Uploading...' : 'Post Story'}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          )}
          
          {stories.map((story) => (
            <button
              key={story.id}
              data-testid={`story-${story.id}`}
              className="flex-shrink-0 w-20 h-28 rounded-lg overflow-hidden relative group"
              onClick={() => api().post(`/stories/${story.id}/view`)}
            >
              {story.media_type === 'video' ? (
                <video src={story.media_url} className="w-full h-full object-cover" />
              ) : (
                <img src={story.media_url} alt="" className="w-full h-full object-cover" />
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
              <div className="absolute bottom-2 left-2 right-2">
                <p className="text-xs font-medium truncate">{story.author_name}</p>
                <p className="text-[10px] text-zinc-400">{formatDate(story.created_at)}</p>
              </div>
              <div className="absolute top-1 left-1 right-1 h-0.5 bg-white/30 rounded">
                <div className="h-full bg-white rounded" style={{ width: '100%' }} />
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Create Post */}
      {canPost && (
        <Dialog open={newPostOpen} onOpenChange={setNewPostOpen}>
          <DialogTrigger asChild>
            <div 
              data-testid="create-post-trigger"
              className="bg-zinc-900/50 border border-zinc-800 p-4 cursor-pointer hover:border-zinc-700 transition-colors"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-zinc-800 rounded-full flex items-center justify-center">
                  <span className="font-bold">{user?.name?.charAt(0)}</span>
                </div>
                <span className="text-zinc-500">What's on your mind?</span>
              </div>
            </div>
          </DialogTrigger>
          <DialogContent className="bg-zinc-900 border-zinc-800 max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-heading uppercase">Create Post</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <Input
                placeholder="Title"
                value={postForm.title}
                onChange={(e) => setPostForm({ ...postForm, title: e.target.value })}
                data-testid="post-title-input"
                className="bg-zinc-800 border-zinc-700"
              />
              <Textarea
                placeholder="Share your thoughts..."
                value={postForm.content}
                onChange={(e) => setPostForm({ ...postForm, content: e.target.value })}
                data-testid="post-content-input"
                className="bg-zinc-800 border-zinc-700 min-h-[120px]"
              />
              
              <div className="flex gap-2">
                <label className="flex items-center gap-2 px-3 py-2 bg-zinc-800 rounded-sm cursor-pointer hover:bg-zinc-700">
                  <ImageIcon className="w-4 h-4" />
                  <span className="text-sm">Photo</span>
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        const url = await handleFileUpload(file, 'image');
                        if (url) {
                          setPostForm({
                            ...postForm,
                            media_urls: [...postForm.media_urls, url],
                            media_types: [...postForm.media_types, 'image']
                          });
                        }
                      }
                    }}
                  />
                </label>
                <label className="flex items-center gap-2 px-3 py-2 bg-zinc-800 rounded-sm cursor-pointer hover:bg-zinc-700">
                  <Video className="w-4 h-4" />
                  <span className="text-sm">Video</span>
                  <input
                    type="file"
                    accept="video/*"
                    className="hidden"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        const url = await handleFileUpload(file, 'video');
                        if (url) {
                          setPostForm({
                            ...postForm,
                            media_urls: [...postForm.media_urls, url],
                            media_types: [...postForm.media_types, 'video']
                          });
                        }
                      }
                    }}
                  />
                </label>
              </div>

              {postForm.media_urls.length > 0 && (
                <div className="flex gap-2 flex-wrap">
                  {postForm.media_urls.map((url, i) => (
                    <div key={i} className="w-20 h-20 bg-zinc-800 rounded overflow-hidden relative">
                      {postForm.media_types[i] === 'video' ? (
                        <video src={url} className="w-full h-full object-cover" />
                      ) : (
                        <img src={url} alt="" className="w-full h-full object-cover" />
                      )}
                      <button
                        onClick={() => {
                          setPostForm({
                            ...postForm,
                            media_urls: postForm.media_urls.filter((_, j) => j !== i),
                            media_types: postForm.media_types.filter((_, j) => j !== i)
                          });
                        }}
                        className="absolute top-1 right-1 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center text-xs"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}

              <Button
                onClick={handleCreatePost}
                disabled={uploading}
                data-testid="submit-post-btn"
                className="w-full bg-white text-black hover:bg-zinc-200 font-bold uppercase"
              >
                {uploading ? 'Uploading...' : 'Post'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Blog Feed */}
      <div className="space-y-4">
        {blogs.length === 0 ? (
          <div className="text-center py-12 text-zinc-500">
            <p>No posts yet. Be the first to share!</p>
          </div>
        ) : (
          blogs.map((blog) => (
            <article 
              key={blog.id}
              data-testid={`blog-post-${blog.id}`}
              className="bg-zinc-900/50 border border-zinc-800 overflow-hidden"
            >
              {/* Header */}
              <div className="p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-zinc-800 rounded-full flex items-center justify-center overflow-hidden">
                    {blog.author_image ? (
                      <img src={blog.author_image} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <span className="font-bold">{blog.author_name?.charAt(0)}</span>
                    )}
                  </div>
                  <div>
                    <p className="font-medium">{blog.author_name}</p>
                    <p className="text-xs text-zinc-500">{formatDate(blog.created_at)}</p>
                  </div>
                </div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                      <MoreHorizontal className="w-4 h-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="bg-zinc-900 border-zinc-800">
                    <DropdownMenuItem onClick={() => handleShare(blog.id, 'copy')}>
                      <Link2 className="w-4 h-4 mr-2" /> Copy Link
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>

              {/* Content */}
              <div className="px-4 pb-3">
                <h3 className="font-heading font-bold text-lg mb-2">{blog.title}</h3>
                <p className="text-zinc-300 whitespace-pre-wrap">{blog.content}</p>
              </div>

              {/* Media */}
              {blog.media_urls?.length > 0 && (
                <div className={`grid ${blog.media_urls.length > 1 ? 'grid-cols-2' : 'grid-cols-1'} gap-1`}>
                  {blog.media_urls.map((url, i) => (
                    <div key={i} className="aspect-video bg-zinc-800">
                      {blog.media_types?.[i] === 'video' ? (
                        <video src={url} controls className="w-full h-full object-cover" />
                      ) : (
                        <img src={url} alt="" className="w-full h-full object-cover" />
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Stats */}
              <div className="px-4 py-2 flex items-center gap-4 text-sm text-zinc-500 border-t border-zinc-800">
                <span>{blog.likes?.length || 0} likes</span>
                <span>{blog.comments?.length || 0} comments</span>
                <span>{blog.shares || 0} shares</span>
              </div>

              {/* Actions */}
              <div className="px-4 py-2 flex items-center border-t border-zinc-800">
                <Button
                  variant="ghost"
                  onClick={() => handleLike(blog.id)}
                  data-testid={`like-btn-${blog.id}`}
                  className={`flex-1 ${blog.likes?.includes(user?.id) ? 'text-red-500' : 'text-zinc-400'}`}
                >
                  <Heart className={`w-5 h-5 mr-2 ${blog.likes?.includes(user?.id) ? 'fill-current' : ''}`} />
                  Like
                </Button>
                <Button variant="ghost" className="flex-1 text-zinc-400">
                  <MessageCircle className="w-5 h-5 mr-2" />
                  Comment
                </Button>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" className="flex-1 text-zinc-400">
                      <Share2 className="w-5 h-5 mr-2" />
                      Share
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="bg-zinc-900 border-zinc-800">
                    <DropdownMenuItem onClick={() => handleShare(blog.id, 'facebook')}>
                      <Facebook className="w-4 h-4 mr-2 text-blue-500" /> Facebook
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => handleShare(blog.id, 'twitter')}>
                      <Twitter className="w-4 h-4 mr-2 text-sky-500" /> Twitter
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => handleShare(blog.id, 'linkedin')}>
                      <Linkedin className="w-4 h-4 mr-2 text-blue-600" /> LinkedIn
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => handleShare(blog.id, 'copy')}>
                      <Link2 className="w-4 h-4 mr-2" /> Copy Link
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>

              {/* Comments Preview */}
              {blog.comments?.length > 0 && (
                <div className="px-4 pb-4 space-y-2">
                  {blog.comments.slice(0, 2).map((comment) => (
                    <div key={comment.id} className="flex gap-2">
                      <span className="font-medium text-sm">{comment.author_name}</span>
                      <span className="text-sm text-zinc-400">{comment.content}</span>
                    </div>
                  ))}
                </div>
              )}
            </article>
          ))
        )}
      </div>
    </div>
  );
};
